import { ThaiSyllable } from '../models/thai-syllable'
import rtgsTlitScheme from '../translit-schemes/rtgs-tlit-scheme'
import transliterateVowel from '../thai-syllables/transliteration/transliterate-vowel'

// Letter-faithful finals for reconstructing an English loanword's spelling.
// Unlike the phonetic schemes (where a final ล is pronounced and romanized
// "n"), a loanword's final letters stand for the English source letters:
// the ล in เรียล is the l of "real". Keyed by single consonant so it also
// covers garan-silenced letters, which can be any consonant (เดย์ → ย).
const LETTER_FAITHFUL_FINALS: Record<string, string> = {
   ก: 'k',
   ข: 'k',
   ฃ: 'k',
   ค: 'k',
   ฅ: 'k',
   ฆ: 'k',
   ง: 'ng',
   จ: 'ch',
   ฉ: 'ch',
   ช: 'ch',
   ซ: 's',
   ฌ: 'ch',
   ญ: 'n',
   ฎ: 'd',
   ฏ: 't',
   ฐ: 't',
   ฑ: 't',
   ฒ: 't',
   ณ: 'n',
   ด: 'd',
   ต: 't',
   ถ: 't',
   ท: 't',
   ธ: 't',
   น: 'n',
   บ: 'b',
   ป: 'p',
   ผ: 'p',
   ฝ: 'f',
   พ: 'p',
   ฟ: 'f',
   ภ: 'p',
   ม: 'm',
   ย: 'y',
   ร: 'r',
   ล: 'l',
   ว: 'w',
   ศ: 's',
   ษ: 's',
   ส: 's',
   ห: 'h',
   ฬ: 'l',
   อ: '',
   ฮ: 'h',
}

/**
 * Romanizes a syllable the way its English source word was likely spelled,
 * for guessing the English behind an out-of-dictionary loanword. Reuses the
 * RTGS start-consonant and vowel maps, but writes finals letter-faithfully
 * and keeps garan-silenced consonants in their spelling positions:
 * เรียล → "rial", ไทม์ → "thaim", ซอฟต์ → "soft", เบิร์ด → "boerd".
 */
// Common Thai loanword spelling patterns whose English source is spelled
// differently than the letter-by-letter reading. Each rule turns a
// syllable's default romanization into one extra candidate; the matcher
// tries the default first, so these only ever add recall. Most patterns
// belong in the loanword-key collapse instead (ไ-ม์ → "ime" and
// เ-ียร์ → "ear"/"eer" already land on their English keys there) — an
// entry here is for spellings the shared collapse can't express.
const ROMAN_ALTERNATES: [RegExp, string][] = [
   // A leading ส spells an English s-cluster: สปอร์ต (สป → "sop") is
   // "sport", lone ส before สโลว์-style clusters romanizes "so"
   [/^so(?=[^aeiou]|$)/, 's'],
]

/**
 * Extra plausible romanizations for a syllable, given its default one.
 * May be empty; never contains the default itself.
 */
export const guessRomanizationAlternates = (romanized: string): string[] =>
   ROMAN_ALTERNATES.filter(([pattern]) => pattern.test(romanized)).map(([pattern, replacement]) =>
      romanized.replace(pattern, replacement)
   )

const romanizeSyllableForGuess = (syllable: ThaiSyllable): string => {
   const { startConsonant, endConsonant, garanAfterVowel, garanAfterEndConsonant } = syllable

   const start = rtgsTlitScheme.startConsonantsTranslit[startConsonant]
   const vowel = transliterateVowel(syllable, rtgsTlitScheme)
   const final = (letter: string | undefined): string =>
      letter ? (LETTER_FAITHFUL_FINALS[letter] ?? '') : ''

   // Spelling order: the garan written after the vowel sits before any end
   // consonant (เบิร์ด = b + oe + r + d), the other garan after it.
   return `${start}${vowel}${final(garanAfterVowel)}${final(endConsonant)}${final(garanAfterEndConsonant)}`
}

export default romanizeSyllableForGuess
